import { useQuery } from '@tanstack/react-query'
import { useRecoilValue } from 'recoil'

import { useNodeApi } from '../api-provider'
import { selectedRoleState } from '../state'

const QUERY_KEY = 'getTableTaskTriggerConfig'

export function useGetInitialTableTaskEntities({
  tableTaskDefinitionName,
  tableId,
}: {
  tableTaskDefinitionName: string
  tableId: string
}) {
  const nodeApi = useNodeApi()
  const selectedRoleName = useRecoilValue(selectedRoleState).name

  return useQuery({
    queryKey: [QUERY_KEY, selectedRoleName, tableTaskDefinitionName],
    queryFn: async () => {
      const tableTaskDefinitionResult = await nodeApi?.urqlClient
        ?.query(listTaskDefinitionItemsQuery, {
          filter: { name: { eq: tableTaskDefinitionName } },
        })
        .toPromise()
      if (tableTaskDefinitionResult?.error) {
        throw new Error(tableTaskDefinitionResult.error.message)
      }

      if (!tableTaskDefinitionResult?.data?.listVendia_TaskDefinitionItems?.Vendia_TaskDefinitionItems?.length) {
        throw new Error('No task definition found for the table')
      }

      const tableTaskTriggerConfigResult = await nodeApi?.urqlClient
        ?.query(listTableTaskTriggerConfigItemsQuery, {
          filter: { tableId: { eq: tableId } },
        })
        .toPromise()

      if (tableTaskTriggerConfigResult?.error) {
        throw new Error(tableTaskTriggerConfigResult.error.message)
      }

      if (
        !tableTaskTriggerConfigResult?.data?.listVendia_TableTaskTriggerConfigItems?.Vendia_TableTaskTriggerConfigItems
          ?.length
      ) {
        throw new Error('No task trigger config found for the table')
      }

      return {
        taskDefinition: tableTaskDefinitionResult.data.listVendia_TaskDefinitionItems.Vendia_TaskDefinitionItems[0],
        tableTaskTriggerConfig:
          tableTaskTriggerConfigResult.data.listVendia_TableTaskTriggerConfigItems
            .Vendia_TableTaskTriggerConfigItems[0],
      }
    },
    enabled: !!selectedRoleName && !!tableTaskDefinitionName && !!tableId,
    staleTime: 1000 * 300, // 5 minutes
  })
}

const listTaskDefinitionItemsQuery = /* GraphQL */ `
  query listTaskDefinitionItems($filter: Vendia_TaskDefinition_FilterInput_) {
    listVendia_TaskDefinitionItems(filter: $filter) {
      Vendia_TaskDefinitionItems {
        _id
        _owner
        name
        script
        sources {
          connectionId
          identifier
          type
        }
        taskConfig {
          priority
        }
        type
        typeId
      }
    }
  }
`

const listTableTaskTriggerConfigItemsQuery = /* GraphQL */ `
  query listTableTaskTriggerConfigItems($filter: Vendia_TableTaskTriggerConfig_FilterInput_) {
    listVendia_TableTaskTriggerConfigItems(filter: $filter) {
      Vendia_TableTaskTriggerConfigItems {
        _id
        _owner
        chainConfig {
          sourceChainMode
          taskId
        }
        schedule {
          cron
          taskId
        }
        tableId
      }
    }
  }
`
